import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Select,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { ReloadOutlined } from "@ant-design/icons";
import {
  adminLoginAsPartner,
  forecastingGetGlobalTasks,
  forecastingGetTasks,
  listSites,
  optimizationsGetTasks,
  predictionsGetTasks,
} from "../../../services/client";
import type {
  GlobalForecastingTaskKind,
  PaginatedResponseSiteReadable,
  PaginatedResponseTaskListItemDto,
  PartnerOutput,
  SiteReadable,
  Status,
  TaskListItemDto,
} from "../../../services/client/types.gen";
import { GcloudLinkButton } from "../../o11y/GcloudLinkButton";
import { taskLogsUrl } from "../../../util/gcloud";
import { extractErrorMessage } from "../../../util/extractErrorMessage";
import { formatDateTime } from "../../../util/formatDate";

const { Text } = Typography;

const SITE_TASK_KIND_LABELS = {
  optimization: "Optimization",
  prediction: "Prediction",
  forecasting: "Forecasting",
} as const;

type SiteTaskKind = keyof typeof SITE_TASK_KIND_LABELS;

const GLOBAL_TASK_KIND_LABELS: Record<GlobalForecastingTaskKind, string> = {
  day_ahead_forecasting: "Day-ahead forecasting",
  imbalance_price_training: "Imbalance training",
  imbalance_price_prediction: "Imbalance prediction",
};

type TaskKind = SiteTaskKind | GlobalForecastingTaskKind;

const TASK_KIND_LABELS: Record<TaskKind, string> = {
  ...SITE_TASK_KIND_LABELS,
  ...GLOBAL_TASK_KIND_LABELS,
};

const TASK_KIND_COLORS: Record<TaskKind, string> = {
  optimization: "purple",
  prediction: "geekblue",
  forecasting: "cyan",
  day_ahead_forecasting: "gold",
  imbalance_price_training: "magenta",
  imbalance_price_prediction: "orange",
};

const STATUS_COLORS: Record<Status, string> = {
  PENDING: "default",
  STARTED: "processing",
  DONE: "success",
  FAILED: "error",
  CANCELLED: "warning",
  TIMEOUT: "volcano",
  SKIPPED: "purple",
};

type BoardTask = TaskListItemDto & {
  kind: TaskKind;
  site_id?: string;
};

type TaskSource =
  | { kind: SiteTaskKind; siteId: string }
  | { kind: GlobalForecastingTaskKind; siteId?: undefined };

const STATUS_VALUES: Status[] = [
  "PENDING",
  "STARTED",
  "DONE",
  "FAILED",
  "CANCELLED",
  "TIMEOUT",
  "SKIPPED",
];

// /admin sits outside the AuthenticationGuard, so the client has no token
// configured and the admin token must be attached per request.
const adminAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("admin-token")}`,
});

const listAllSites = async (): Promise<SiteReadable[]> => {
  const sites: SiteReadable[] = [];
  let after: string | undefined = undefined;
  do {
    const page = await listSites({
      throwOnError: true,
      headers: adminAuthHeaders(),
      query: { page_size: 100, after },
    });
    const body: PaginatedResponseSiteReadable = page.data;
    sites.push(...body.data);
    after = body.has_next_page ? (body.end_cursor ?? undefined) : undefined;
  } while (after);
  return sites;
};

const SITE_TASK_FETCHERS: Record<SiteTaskKind, typeof optimizationsGetTasks> = {
  optimization: optimizationsGetTasks,
  prediction: predictionsGetTasks,
  forecasting: forecastingGetTasks,
};

const TASKS_PAGE_SIZE = 100;
const DISPLAY_PAGE_SIZE = 20;

const fetchSourcePage = async (
  source: TaskSource,
  after: string | undefined,
): Promise<PaginatedResponseTaskListItemDto> => {
  const request = {
    throwOnError: true,
    headers: adminAuthHeaders(),
    query: { page_size: TASKS_PAGE_SIZE, after },
  } as const;
  if (source.siteId === undefined) {
    const response = await forecastingGetGlobalTasks({
      ...request,
      path: { kind: source.kind },
    });
    return response.data;
  }
  const response = await SITE_TASK_FETCHERS[source.kind]({
    ...request,
    path: { site_id: source.siteId },
  });
  return response.data;
};

// Fetches a single page of tasks for one source — the caller decides
// whether and when to fetch the next page, instead of draining the cursor.
const fetchTasksPage = async (
  source: TaskSource,
  after: string | undefined,
): Promise<{ tasks: BoardTask[]; hasNextPage: boolean; after?: string }> => {
  const body = await fetchSourcePage(source, after);
  return {
    tasks: body.data.map((task) => ({
      ...task,
      kind: source.kind,
      site_id: source.siteId,
    })),
    hasNextPage: body.has_next_page ?? false,
    after: body.end_cursor ?? undefined,
  };
};

// Caps how many sites are queried at once so an admin with many sites
// doesn't fire off hundreds of simultaneous requests.
const SITE_FETCH_CONCURRENCY = 8;

const mapWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> => {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;
  const workers = Array.from(
    { length: Math.min(limit, items.length) },
    async () => {
      while (nextIndex < items.length) {
        const currentIndex = nextIndex++;
        results[currentIndex] = await fn(items[currentIndex]);
      }
    },
  );
  await Promise.all(workers);
  return results;
};

const SITE_TASK_KINDS = Object.keys(SITE_TASK_KIND_LABELS) as SiteTaskKind[];
const GLOBAL_TASK_KINDS = Object.keys(
  GLOBAL_TASK_KIND_LABELS,
) as GlobalForecastingTaskKind[];
const TASK_KINDS: TaskKind[] = [...SITE_TASK_KINDS, ...GLOBAL_TASK_KINDS];

type TaskSourceCursor = {
  source: TaskSource;
  after?: string;
  hasNextPage: boolean;
};

const cursorKey = ({ kind, siteId }: TaskSource) =>
  `${siteId ?? "global"}:${kind}`;

// Fetches one page of tasks per source (a site/kind pair, or a global kind) up
// front, then only fetches further pages — for whichever sources still have
// more — when the admin actually pages far enough to need them.
const useTasksAcrossSites = (sites: SiteReadable[] | undefined) => {
  const [tasks, setTasks] = useState<BoardTask[]>([]);
  const [cursors, setCursors] = useState<Map<string, TaskSourceCursor>>(
    new Map(),
  );
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!sites) {
      return;
    }
    let cancelled = false;
    setIsInitialLoading(true);
    setError(null);
    const sources: TaskSource[] = [
      ...GLOBAL_TASK_KINDS.map((kind) => ({ kind })),
      ...sites.flatMap((site) =>
        SITE_TASK_KINDS.map((kind) => ({ kind, siteId: site.site_id })),
      ),
    ];
    mapWithConcurrency(sources, SITE_FETCH_CONCURRENCY, async (source) => {
      const page = await fetchTasksPage(source, undefined);
      return { source, ...page };
    })
      .then((results) => {
        if (cancelled) {
          return;
        }
        const nextCursors = new Map<string, TaskSourceCursor>();
        const allTasks: BoardTask[] = [];
        for (const result of results) {
          allTasks.push(...result.tasks);
          nextCursors.set(cursorKey(result.source), {
            source: result.source,
            after: result.after,
            hasNextPage: result.hasNextPage,
          });
        }
        setTasks(allTasks);
        setCursors(nextCursors);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsInitialLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sites]);

  const hasMore = useMemo(
    () => [...cursors.values()].some((cursor) => cursor.hasNextPage),
    [cursors],
  );

  const fetchMore = useCallback(async () => {
    const pending = [...cursors.values()].filter(
      (cursor) => cursor.hasNextPage,
    );
    if (pending.length === 0) {
      return;
    }
    setIsFetchingMore(true);
    try {
      const results = await mapWithConcurrency(
        pending,
        SITE_FETCH_CONCURRENCY,
        async (cursor) => {
          const page = await fetchTasksPage(cursor.source, cursor.after);
          return { source: cursor.source, ...page };
        },
      );
      setCursors((prev) => {
        const next = new Map(prev);
        for (const result of results) {
          next.set(cursorKey(result.source), {
            source: result.source,
            after: result.after,
            hasNextPage: result.hasNextPage,
          });
        }
        return next;
      });
      setTasks((prev) => [...prev, ...results.flatMap((r) => r.tasks)]);
    } catch (e) {
      setError(e);
    } finally {
      setIsFetchingMore(false);
    }
  }, [cursors]);

  return { tasks, hasMore, isInitialLoading, isFetchingMore, fetchMore, error };
};

type TasksBoardProps = {
  partners?: PartnerOutput[];
};

export const TasksBoard: React.FC<TasksBoardProps> = ({ partners }) => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<Status | "all">("all");
  const [typeFilter, setTypeFilter] = useState<TaskKind | "all">("all");
  const [partnerFilter, setPartnerFilter] = useState<string>("all");
  const [siteFilter, setSiteFilter] = useState<string>("all");

  const {
    data: sites,
    isPending: isSitesPending,
    isFetching: isSitesFetching,
    error: sitesError,
    refetch: refetchSites,
  } = useQuery({
    queryKey: ["admin-sites"],
    retry: false,
    queryFn: listAllSites,
  });

  const {
    tasks,
    hasMore,
    isInitialLoading: isTasksLoading,
    isFetchingMore,
    fetchMore,
    error: tasksError,
  } = useTasksAcrossSites(sites);

  const partnerNameById = useMemo(
    () =>
      Object.fromEntries(
        (partners ?? []).map((partner) => [partner.partner_id, partner.name]),
      ),
    [partners],
  );

  const siteById = useMemo(
    () => Object.fromEntries((sites ?? []).map((site) => [site.site_id, site])),
    [sites],
  );

  const siteOptions = useMemo(
    () =>
      (sites ?? [])
        .filter(
          (site) =>
            partnerFilter === "all" || site.partner_id === partnerFilter,
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [sites, partnerFilter],
  );

  const isFiltered =
    statusFilter !== "all" ||
    typeFilter !== "all" ||
    partnerFilter !== "all" ||
    siteFilter !== "all";

  const filteredTasks = useMemo(
    () =>
      tasks
        .filter(
          (task) => statusFilter === "all" || task.status === statusFilter,
        )
        .filter((task) => typeFilter === "all" || task.kind === typeFilter)
        .filter(
          (task) =>
            partnerFilter === "all" || task.partner_id === partnerFilter,
        )
        .filter((task) => siteFilter === "all" || task.site_id === siteFilter)
        .sort(
          (a, b) =>
            new Date(b.scheduled_at).getTime() -
            new Date(a.scheduled_at).getTime(),
        ),
    [tasks, statusFilter, typeFilter, partnerFilter, siteFilter],
  );

  const [currentPage, setCurrentPage] = useState(1);
  const displayPageCount =
    Math.ceil(filteredTasks.length / DISPLAY_PAGE_SIZE) || 1;

  // Only fetch the next batch of tasks once the admin has paged close to the
  // end of what's already loaded, and never while a filter narrows the view.
  useEffect(() => {
    if (
      !isFiltered &&
      hasMore &&
      !isFetchingMore &&
      currentPage >= displayPageCount - 1
    ) {
      fetchMore();
    }
  }, [
    currentPage,
    displayPageCount,
    hasMore,
    isFetchingMore,
    isFiltered,
    fetchMore,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, typeFilter, partnerFilter, siteFilter]);

  const openTaskAsPartner = useMutation({
    mutationFn: (task: BoardTask) =>
      adminLoginAsPartner({
        throwOnError: true,
        headers: { ...adminAuthHeaders(), partner_id: task.partner_id },
      }),
    onSuccess: ({ data }, task) => {
      localStorage.setItem("token", data.access_token);
      navigate(`/optimizations/${task.id}`);
    },
    onError: () => {
      message.error("Failed to open task");
    },
  });

  const columns: ColumnsType<BoardTask> = [
    { title: "Task ID", dataIndex: "task_id", key: "task_id" },
    {
      title: "Type",
      dataIndex: "kind",
      key: "kind",
      render: (kind: TaskKind) => (
        <Tag color={TASK_KIND_COLORS[kind]}>{TASK_KIND_LABELS[kind]}</Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: Status) => (
        <Tag color={STATUS_COLORS[status]}>{status}</Tag>
      ),
    },
    {
      title: "Partner",
      dataIndex: "partner_id",
      key: "partner_id",
      render: (partnerId: string) => (
        <>
          <Text>{partnerNameById[partnerId] ?? partnerId}</Text>
          {partnerNameById[partnerId] && (
            <Text type="secondary" className="block break-all">
              {partnerId}
            </Text>
          )}
        </>
      ),
    },
    {
      title: "Site",
      dataIndex: "site_id",
      key: "site_id",
      render: (siteId?: string) =>
        siteId === undefined ? (
          <Text type="secondary">Global</Text>
        ) : (
          <>
            <Text>{siteById[siteId]?.name ?? siteId}</Text>
            <Text type="secondary" className="block break-all">
              {siteId}
            </Text>
          </>
        ),
    },
    {
      title: "Scheduled",
      dataIndex: "scheduled_at",
      key: "scheduled_at",
      render: (date: string) => formatDateTime(date),
    },
    {
      title: "",
      key: "actions",
      render: (_, task) => (
        <div className="flex flex-wrap gap-2">
          <GcloudLinkButton href={taskLogsUrl(task.id)} size="small">
            Logs
          </GcloudLinkButton>
          {task.kind === "optimization" && task.status === "DONE" && (
            <Button
              size="small"
              type="primary"
              loading={
                openTaskAsPartner.isPending &&
                openTaskAsPartner.variables?.id === task.id
              }
              onClick={() => openTaskAsPartner.mutate(task)}
            >
              Details
            </Button>
          )}
        </div>
      ),
    },
  ];

  const boardError = sitesError ?? tasksError;

  if (boardError) {
    return (
      <Alert
        type="error"
        showIcon
        message="Failed to load tasks"
        description={extractErrorMessage(boardError)}
      />
    );
  }

  if (isSitesPending || isTasksLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <Text type="secondary" className="max-w-2xl">
          Every optimization, prediction and forecasting task across all
          partners and sites, plus the global day-ahead and imbalance price
          forecasting tasks.
        </Text>
        <Button
          icon={<ReloadOutlined />}
          loading={isSitesFetching}
          onClick={() => refetchSites()}
        >
          Refresh
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select<Status | "all">
          value={statusFilter}
          onChange={setStatusFilter}
          className="w-40"
          options={[
            { label: "All statuses", value: "all" },
            ...STATUS_VALUES.map((status) => ({
              label: status,
              value: status,
            })),
          ]}
        />
        <Select<TaskKind | "all">
          value={typeFilter}
          onChange={setTypeFilter}
          className="w-40"
          options={[
            { label: "All types", value: "all" },
            ...TASK_KINDS.map((kind) => ({
              label: TASK_KIND_LABELS[kind],
              value: kind,
            })),
          ]}
        />
        <Select<string>
          value={partnerFilter}
          onChange={(value) => {
            setPartnerFilter(value);
            setSiteFilter("all");
          }}
          className="w-64"
          showSearch
          optionFilterProp="label"
          options={[
            { label: "All partners", value: "all" },
            ...(partners ?? []).map((partner) => ({
              label: `${partner.name} (${partner.partner_id})`,
              value: partner.partner_id,
            })),
          ]}
        />
        <Select<string>
          value={siteFilter}
          onChange={setSiteFilter}
          className="w-64"
          showSearch
          optionFilterProp="label"
          options={[
            { label: "All sites", value: "all" },
            ...siteOptions.map((site) => ({
              label: `${site.name} (${site.site_id})`,
              value: site.site_id,
            })),
          ]}
        />
      </div>

      <Table<BoardTask>
        rowKey={(task) => `${task.kind}:${task.id}`}
        dataSource={filteredTasks}
        columns={columns}
        loading={isFetchingMore}
        pagination={{
          current: currentPage,
          pageSize: DISPLAY_PAGE_SIZE,
          total: isFiltered
            ? undefined
            : filteredTasks.length + (hasMore ? 1 : 0),
          showSizeChanger: false,
          onChange: setCurrentPage,
        }}
      />
    </div>
  );
};
